import type { IUser } from '@featit/shared';
import { observer } from 'mobx-react-lite';
import { useId } from 'react';
import { NavLink } from 'react-router-dom';
import { useStores } from '@/hooks/useStores';
import { useT } from '@/i18n/LocaleProvider';
import { useFormat } from '@/i18n/useFormat';
import { Avatar } from './Avatar';

/**
 * F-0777 (Jay, 2026-10-31: "move the profile menu as icon only into the bottom row"): the person's
 * avatar, one 32 px icon of the sidebar's bottom row (`[data-sidebar-account-actions]`), opening the
 * profile. It replaced the line of its own above the row that held the avatar and the name.
 *
 * Its accessible name and tooltip are "Profile — <name>": the icon alone says neither whose it is nor
 * where it goes. I-2233 (AC-PI.2): while invitations wait, their count stands on the avatar's corner
 * and the link's accessible description says how many and where they are (Settings → Invitations).
 * The count is part of this one link, not a link of its own: a second target over the avatar's
 * corner would take a finger's press meant for the profile, and the row has no room beside it at
 * the sidebar's narrowest (F-0585 PSW-7). Invitations stay a press away in Settings' rail, and the
 * invitation toast and the Get started notice link there too.
 */
export const SidebarProfileLink = observer(function SidebarProfileLink({
  user,
  to,
  onNavigate,
}: {
  user: IUser;
  to: string;
  onNavigate?: () => void;
}) {
  const t = useT();
  const format = useFormat();
  const descriptionId = useId();
  const count = useStores()?.invitationsWaitingStore?.count;
  const label = t('navigation.sidebar.profileOf', { name: user.name });
  const waiting = count ? t('navigation.sidebar.invitationsWaitingOnProfile', { count }) : null;
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      data-sidebar-profile=""
      aria-label={label}
      aria-describedby={waiting ? descriptionId : undefined}
      title={waiting ? `${label}\n${waiting}` : label}
      className={({ isActive }) =>
        `coarse-hit relative grid h-8 w-8 shrink-0 place-items-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-focus ${
          isActive
            ? 'bg-[#D7DBDC]/75 dark:bg-[#303030]'
            : 'hover:bg-[#D7DBDC]/50 dark:hover:bg-[#242424]'
        }`
      }
    >
      <Avatar user={user} size="xs" />
      {waiting ? (
        <>
          {/*
            The bell's own badge colours, white on red in both modes: no `data-accent-count`, whose
            Colour blue (ACC-5) is drawn for a count on a grey chip and is unreadable on this red.
          */}
          <span
            aria-hidden="true"
            data-testid="invitations-waiting-badge"
            className="pointer-events-none absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--evx-badge)] px-1 text-[10px] font-bold leading-none tabular-nums text-[var(--evx-on-badge)] ring-2 ring-[#FEFEFE] dark:ring-[#212121]"
          >
            {format.number(count!)}
          </span>
          <span id={descriptionId} hidden>
            {waiting}
          </span>
        </>
      ) : null}
    </NavLink>
  );
});
